import { useEffect, useRef } from "react";
import { getInProgressJobs } from "../services/jobs.service";
import { mapJobToPushedJob } from "../utils/job-mapping.utils";
import type { PushedJob } from "../types/jobs.types";

/**
 * Fetches in-progress jobs on mount and seeds them into the counter.
 * Resets the counter when organizationId changes to prevent cross-org leaks.
 *
 * The seededOrgRef guard prevents the double-fetch that StrictMode causes by
 * running effects twice. The guard is checked BEFORE reset so that a genuine
 * org change (different id) always triggers a reset + re-seed, while a
 * StrictMode re-mount of the same org is skipped after the first fetch.
 *
 * Call order in JobProvider: after useJobUpdates so the Pusher subscription
 * is established before this fetch completes (avoids missing a completion push).
 */
export const useInProgressJobsSeed = (
  seedInProgressJobs: (jobs: PushedJob[]) => void,
  resetJobs: () => void,
  organizationId: string,
): void => {
  const seededOrgRef = useRef<string | null>(null);

  useEffect(() => {
    if (seededOrgRef.current === organizationId) return;

    resetJobs();
    seededOrgRef.current = organizationId;

    if (!organizationId) return;

    const expectedOrg = organizationId;
    getInProgressJobs()
      .then(({ data }) => {
        // Drop a response that resolves after the org changed (or logged out):
        // seeding it would leak the previous org's jobs into the current counter.
        if (seededOrgRef.current !== expectedOrg) return;
        seedInProgressJobs(data.jobs.map(mapJobToPushedJob));
      })
      .catch(() => {
        // Non-critical: counter starts at 0 and fills via live pushes.
      });
  }, [organizationId, seedInProgressJobs, resetJobs]);
};
